import {
  BLACK_DIE_SHAPES,
  type CarnivalRngState,
  DIE_FACES,
  type Die,
  type DieId,
} from "../types";
import { nextBounded, nextDieFace } from "./rng";

/** Dice cluster inside the seat ring (seats sit at radius 1.0 — see board.ts). */
const DIE_RADIUS_MAX = 0.65;
const TWO_PI = Math.PI * 2;

export interface DiceRoll {
  readonly rngState: CarnivalRngState;
  readonly dice: readonly Die[];
}

/**
 * Rolls all 7 dice (6 distinct black shapes + white d6) and assigns each a
 * seeded position on the virtual circular table. Draw order is fixed
 * (value then angle then radius, per die, in BLACK_DIE_SHAPES order, white
 * last) so replays from the same seed are reproducible.
 */
export function rollAllDice(initialRngState: CarnivalRngState): DiceRoll {
  let rngState = initialRngState;
  const dice: Die[] = [];

  for (const shape of BLACK_DIE_SHAPES) {
    const valueDraw = nextDieFace(rngState, DIE_FACES[shape]);
    rngState = valueDraw.rngState;
    const angleDraw = nextBounded(rngState, TWO_PI);
    rngState = angleDraw.rngState;
    const radiusDraw = nextBounded(rngState, DIE_RADIUS_MAX);
    rngState = radiusDraw.rngState;
    dice.push({
      id: shape,
      color: "black",
      value: valueDraw.value,
      position: { angle: angleDraw.value, radius: radiusDraw.value },
      lockedBy: null,
    });
  }

  const whiteValueDraw = nextDieFace(rngState, 6);
  rngState = whiteValueDraw.rngState;
  const whiteAngleDraw = nextBounded(rngState, TWO_PI);
  rngState = whiteAngleDraw.rngState;
  const whiteRadiusDraw = nextBounded(rngState, DIE_RADIUS_MAX);
  rngState = whiteRadiusDraw.rngState;
  dice.push({
    id: "white",
    color: "white",
    value: whiteValueDraw.value,
    position: { angle: whiteAngleDraw.value, radius: whiteRadiusDraw.value },
    lockedBy: null,
  });

  return { rngState, dice };
}

export interface RerollResult {
  readonly rngState: CarnivalRngState;
  readonly dice: readonly Die[];
}

/** Re-rolls one black die's value in place (position and lock unchanged). */
export function rerollDie(
  rngState: CarnivalRngState,
  dice: readonly Die[],
  dieId: DieId,
): RerollResult {
  if (dieId === "white") throw new Error("The white die can never be rerolled");
  const draw = nextDieFace(rngState, DIE_FACES[dieId]);
  return {
    rngState: draw.rngState,
    dice: dice.map((die) =>
      die.id === dieId ? { ...die, value: draw.value } : die,
    ),
  };
}

export function findDie(dice: readonly Die[], dieId: DieId): Die | undefined {
  return dice.find((die) => die.id === dieId);
}

export function getUnlockedBlackDice(dice: readonly Die[]): readonly Die[] {
  return dice.filter((die) => die.color === "black" && die.lockedBy === null);
}
